/**
 * DevicesManagement - Standalone Devices Management Component
 *
 * Manages devices with CRUD operations using DataCard for display.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import DevicesModal from './modals/DevicesModal';
import EntityCSVUploadButton from './EntityCSVUploadButton';
import type { Device } from '@/types/associations';

export default function DevicesManagement() {
  // Entity state
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Modal state
  const [modalState, setModalState] = useState<{ show: boolean; editing: Device | null }>({
    show: false,
    editing: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAdminDevices();
      if (!response.error && response.data) {
        setDevices(response.data as Device[]);
      }
    } catch (error) {
      toast.error('Failed to load devices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD handlers
  const handleSave = async (data: Partial<Device>) => {
    if (modalState.editing) {
      const response = await apiClient.updateAdminDevice(modalState.editing.id, data);
      if (response.error) throw new Error(response.error);
      toast.success('Device updated successfully');
    } else {
      if (!data.device_id) {
        throw new Error('Device ID is required');
      }
      const extendedData = data as Partial<Device> & { is_active?: boolean };
      const response = await apiClient.createAdminDevice({
        device_id: data.device_id,
        name: data.name,
        model: data.model,
        manufacturer: data.manufacturer,
        description: data.description,
        is_active: extendedData.is_active ?? true,
      });
      if (response.error) throw new Error(response.error);
      toast.success('Device created successfully');
    }
    await loadData();
  };

  const handleDelete = (id: number) => {
    const device = devices.find((d) => d.id === id);
    setConfirmModal({
      show: true,
      title: 'Delete Device',
      message: `Are you sure you want to delete "${device?.device_id}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteAdminDevice(id);
          if (response.error) throw new Error(response.error);
          toast.success('Device deleted successfully');
          setSelectedId(null);
          await loadData();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete device');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Table rendering
  const renderTable = (items: Device[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Device ID</th>
          <th>Name</th>
          <th>Model</th>
          <th>Manufacturer</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((device) => (
          <tr
            key={device.id}
            onClick={() => setSelectedId(selectedId === device.id ? null : device.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedId === device.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><strong>{device.device_id}</strong></td>
            <td className="text-muted small">{device.name || '-'}</td>
            <td className="text-muted small">{device.model || '-'}</td>
            <td className="text-muted small">{device.manufacturer || '-'}</td>
            <td>
              <span className={`badge ${device.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                {device.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading devices...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Devices Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage devices for data collection
          </p>
        </div>
      </div>

      <DataCard
        cardKey="devices"
        title="Devices"
        badgeCount={devices.length}
        data={devices}
        itemsPerPage={10}
        searchPlaceholder="Search devices..."
        filterFunction={(item, searchTerm) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          return (
            item.device_id.toLowerCase().includes(term) ||
            (item.name || '').toLowerCase().includes(term) ||
            (item.model || '').toLowerCase().includes(term) ||
            (item.manufacturer || '').toLowerCase().includes(term)
          );
        }}
        headerActions={
          <>
            {selectedId !== null && (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    const device = devices.find((d) => d.id === selectedId);
                    if (device) setModalState({ show: true, editing: device });
                  }}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    if (selectedId !== null) handleDelete(selectedId);
                  }}
                  className="me-2"
                >
                  Delete
                </Button>
              </>
            )}
            <EntityCSVUploadButton
              entityType="devices"
              onUploadComplete={loadData}
              size="sm"
              variant="outline-success"
              className="me-2"
            />
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalState({ show: true, editing: null })}
            >
              Create
            </Button>
          </>
        }
        renderTable={renderTable}
        onRefresh={loadData}
        maxHeight="600px"
        emptyMessage="No devices found. Create your first device."
      />

      {/* Device Modal */}
      <DevicesModal
        show={modalState.show}
        device={modalState.editing}
        onHide={() => setModalState({ show: false, editing: null })}
        onSave={handleSave}
        onRefresh={loadData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
